import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import './Global.css';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: '',
  });

  const pagination = usePagination(logs, 25);
  const { setPage } = pagination;
  const currentLogs = pagination.pageItems;

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLogs = async () => {
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await api.get('/audit/logs', { params });
      setLogs(response.data);
      setPage(1);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = async () => {
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await api.get('/audit/export', { params });

      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_logs_${new Date().toISOString()}.json`;
      link.click();

      toast.success('Logs exportados com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar logs:', error);
      toast.error('Erro ao exportar logs');
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      CREATE_ENTRY: 'Criou Registro',
      UPDATE_ENTRY: 'Atualizou Registro',
      DELETE_ENTRY: 'Excluiu Registro',
      GENERATE_REPORT: 'Gerou Relatório',
      CREATE_USER: 'Criou Usuário',
      UPDATE_USER: 'Atualizou Usuário',
      DEACTIVATE_USER: 'Desativou Usuário',
      CHANGE_PERMISSION: 'Alterou Permissão',
      FACE_ENROLL: 'Cadastrou Fotos (Facial)',
      FACE_DELETE_PHOTOS: 'Excluiu Fotos (Facial)',
      FACE_DELETE_VERIFICATION: 'Excluiu Verificação Facial',
      UPDATE_FACE_CONFIG: 'Atualizou Config. Facial',
      UPDATE_SCHOOL_LOCATION_CONFIG: 'Atualizou Localização do Colégio',
      CREATE_ESCALA: 'Criou Ciclo da Escala',
      UPDATE_ESCALA: 'Alterou Escala (ciclo/dia)',
      DELETE_ESCALA: 'Excluiu Ciclo da Escala',
      UPDATE_ESCALA_INSCRICAO: 'Decidiu Vaga da Escala',
    };
    return labels[action] || action;
  };

  const getActionBadgeClass = (action) => {
    if (action.includes('CREATE')) return 'badge-success';
    if (action.includes('UPDATE') || action.includes('CHANGE')) return 'badge-info';
    if (action.includes('DELETE') || action.includes('DEACTIVATE')) return 'badge-danger';
    if (action.includes('REPORT')) return 'badge-visualizador';
    return 'badge-user';
  };

  if (loading) return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando logs...</p>
      </div>
    </>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1 style={{ margin: 0 }}>Logs de Auditoria</h1>
            <p className="subtitle">{logs.length} {logs.length === 1 ? 'registro' : 'registros'} encontrado{logs.length === 1 ? '' : 's'}</p>
          </div>
          <div className="header-actions">
            <button className="btn-primary" onClick={exportLogs}>
              Exportar Logs
            </button>
          </div>
        </div>

        <div className="section">
          <h3 className="section-title">Filtros</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Ação</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              >
                <option value="">Todas</option>
                <option value="CREATE_ENTRY">Criou Registro</option>
                <option value="UPDATE_ENTRY">Atualizou Registro</option>
                <option value="DELETE_ENTRY">Excluiu Registro</option>
                <option value="GENERATE_REPORT">Gerou Relatório</option>
                <option value="CREATE_USER">Criou Usuário</option>
                <option value="UPDATE_USER">Atualizou Usuário</option>
                <option value="DEACTIVATE_USER">Desativou Usuário</option>
                <option value="CHANGE_PERMISSION">Alterou Permissão</option>
                <option value="UPDATE_FACE_CONFIG">Atualizou Config. Facial</option>
                <option value="FACE_ENROLL">Cadastrou Fotos (Facial)</option>
                <option value="FACE_DELETE_PHOTOS">Excluiu Fotos (Facial)</option>
                <option value="FACE_DELETE_VERIFICATION">Excluiu Verificação Facial</option>
                <option value="UPDATE_SCHOOL_LOCATION_CONFIG">Atualizou Localização do Colégio</option>
                <option value="CREATE_ESCALA">Criou Ciclo da Escala</option>
                <option value="UPDATE_ESCALA">Alterou Escala (ciclo/dia)</option>
                <option value="DELETE_ESCALA">Excluiu Ciclo da Escala</option>
                <option value="UPDATE_ESCALA_INSCRICAO">Decidiu Vaga da Escala</option>
              </select>
            </div>

            <div className="form-group">
              <label>Data Inicial</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Data Final</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ justifyContent: 'flex-end' }}>
              <button className="btn-primary btn-sm" onClick={loadLogs}>
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Ator</th>
                <th>Ação</th>
                <th>Alvo</th>
                <th>IP</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {currentLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center empty-state">
                    <strong>Nenhum log encontrado</strong>
                    Ajuste o período ou o tipo de ação e aplique os filtros de novo.
                  </td>
                </tr>
              ) : (
                currentLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="col-time">{new Date(log.timestamp).toLocaleString('pt-BR')}</td>
                    <td className="cell-primary">
                      {log.actorName}
                      <span className="cell-sub">{log.actorRole}</span>
                    </td>
                    <td>
                      <span className={`badge ${getActionBadgeClass(log.action)}`}>
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td>{log.targetUserName || '-'}</td>
                    <td><code style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>{log.ipAddress}</code></td>
                    <td>{log.reason || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          id="audit"
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          from={pagination.from}
          to={pagination.to}
          pageSize={pagination.pageSize}
          pageSizeOptions={[25, 50, 100, 200]}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
          itemLabel="registros"
        />
      </div>
    </>
  );
};

export default AuditLogs;
